import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useParams } from 'react-router-dom';
import {
  Cell,
  DateTimeField,
  List,
  ListDataRefresher,
  MassUpdate, SelectAllUpdater,
  useListContext,
  useMassUpdateServiceRequired,
  useShowContextRequired,
} from 'ui-builder';
import { ProjectBreadcrumb } from '../view/ProjectContext';
import { Breadcrumb } from '../../shared/web/layout/Breadcrumbs';
import MainBlock from '../../shared/web/layout/MainBlock';
import TextField from '../../shared/uibuilder/show/field/TextField';
import ProjectPagesButtons from '../view/ProjectPagesButtons';
import InnerBlock from '../../shared/web/layout/InnerBlock';
import { ApplyTemplateButton } from './ApplyTemplateButton';
import DeleteDocumentButton from './DeleteDocumentButton';
import DownloadDocumentButton from './DownloadDocumentButton';
import DeleteFileButton from './DeleteFileButton';
import CreateDocumentButton, { CreateDocumentButtonPortal } from './CreateDocumentButton';
import UploadDocumentButton from './UploadDocumentButton';
import AddLabelsButton, { AddLabelsButtonPortal } from './AddLabelsButton';
import RenameFileButton from './RenameFileButton';
import Right from '../../shared/web/Right';
import OverflowHidden from '../../shared/widgets/OverflowHidden';
import ProjectDocumentsMassUpdateControls from './massupdate/ProjectDocumentsMassUpdateControls';
import ShareDocumentButton from './ShareDocumentButton';
import PreviewDocumentField from './PreviewDocumentField';
import TextLink from '../../shared/widgets/TextLink';

import styles from './ProjectDocumentsPage.module.scss';
import { FileSizeField, GridLayout, SelectForMassUpdateCheckbox } from 'features/nekst-widgets';
import { MassUpdateCheckbox } from 'features/tasks-list-feature';
import { ProjectTypeField, useAssignLabelFormHelper } from 'features/tasks-forms';
import { ProjectPermission, useAuthorizationChecker } from 'authorization-scope';
import {
  DOCUMENTS_UPDATED,
  ProjectDocument,
  useProjectDocumentsService
} from 'nekst-api';
import { get } from 'lodash';
import { SendDocumentsViaEmailToggleWrapper } from 'feature-toggle';
import { SendByEmailAddressWidget } from './SendByEmailAddressWidget';

function AddLabelButton() {
  const assignLabelFormHelper = useAssignLabelFormHelper();
  return (
    <TextLink
      uri="#"
      action={() => {
        assignLabelFormHelper.openForm();
      }}
      className={styles.link}
    >
      + Add Label
    </TextLink>
  );
}

function YesNoField(props: {
  source: string
}) {
  const showContext = useShowContextRequired<ProjectDocument>();

  const data = showContext.data!;

  const value = get(data, props.source);

  if (value) {
    return (<span>Yes</span>);
  } else {
    return (<span>No</span>);
  }
}

function AddedToClientPortalField() {
  const showContext = useShowContextRequired<ProjectDocument>();

  const data = showContext.data!;

  if (data.fileId) {
    return (<YesNoField source="_addedToClientPortal" />);
  } else {
    return (<span>-</span>);
  }
}

function DocumentLabelField(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: {
    // eslint-disable-next-line react/no-unused-prop-types
    label: string,
  },
) {
  const listContext = useListContext<ProjectDocument>();

  const showContext = useShowContextRequired<ProjectDocument>();

  const currentItem = showContext.data;

  const hasUnassignedLabels = () => {
    return listContext.data!.filter((item) => !item._file && item._label).length > 0;
  };

  if (currentItem._label) {
    return (<span>{currentItem._label.name}</span>);
  } else if (hasUnassignedLabels()) {
    return (<AddLabelButton />);
  } else {
    return (<span>-</span>);
  }
}

function ListFields() {
  const massUpdateContext = useMassUpdateServiceRequired();

  const weights = massUpdateContext.isEnabled() ? [1, 4, 5, 2, 3] : [4, 5, 2, 3];

  const { isGranted } = useAuthorizationChecker();

  const hasManagePermission = isGranted(ProjectPermission.CREATE_DOCUMENTS);

  if (hasManagePermission) {
    weights.push(2);
  }

  return (
    <>
      <GridLayout noDataMessage="No Documents created." weights={weights}>
        {massUpdateContext.isEnabled() && <Cell label="" small><SelectForMassUpdateCheckbox /></Cell>}
        <DocumentLabelField label="Label" />
        <Cell label="Document">
          <PreviewDocumentField />
          <RenameFileButton />
          <UploadDocumentButton />
          <DownloadDocumentButton />
          <DeleteFileButton />
        </Cell>
        <FileSizeField source="_file.size" label="Size" />
        <DateTimeField source="uploadedAt" label="Date Uploaded" />
        <Cell label="Added to Client Portal">
          <AddedToClientPortalField />
        </Cell>
        {hasManagePermission && (<Cell label="Actions">
          <ShareDocumentButton />
          <DeleteDocumentButton />
        </Cell>)}
      </GridLayout>
      <SelectAllUpdater />
    </>
  );
}

function ProjectDocumentList(props: {
  projectId: number,
}) {
  const documentService = useProjectDocumentsService();

  return (
    <MassUpdate>
      <List<ProjectDocument>
        getDataFunc={() => documentService.getProjectDocuments(props.projectId)}
      >
        <OverflowHidden>
          <Right>
            <MassUpdateCheckbox />
          </Right>
        </OverflowHidden>
        <ProjectDocumentsMassUpdateControls />
        <ListFields />
        <ListDataRefresher eventName={DOCUMENTS_UPDATED} />
        <CreateDocumentButton projectId={props.projectId} />
        <AddLabelsButton projectId={props.projectId} />
      </List>
    </MassUpdate>
  );
}

export default function ProjectDocumentsPage() {

  const params = useParams<{ id: string }>();

  const projectId = params.id ? parseInt(params.id, 10) : 0;

  return (
    <>
      <ProjectBreadcrumb>
        <Breadcrumb text="Documents" />
      </ProjectBreadcrumb>
      <MainBlock
        title={<TextField source="name" />}
        titleLine2={(
          <>
            <ProjectTypeField />
          </>
        )}
        headerRight={(
          <ProjectPagesButtons />
        )}
      >
        <InnerBlock
          title="Documents"
          titleLine2={(
            <SendDocumentsViaEmailToggleWrapper>
              <SendByEmailAddressWidget projectId={projectId} />
            </SendDocumentsViaEmailToggleWrapper>
          )}
          headerRight={(
            <>
              <AddLabelsButtonPortal />
              <CreateDocumentButtonPortal />
              <ApplyTemplateButton projectId={projectId} />
            </>
          )}
        >
          <ProjectDocumentList projectId={projectId} />
        </InnerBlock>
      </MainBlock>
    </>
  );
}
